import { Autocomplete, type AutocompleteProps, TextField } from '@mui/material'
import { get, type PropertyPath, set } from 'lodash'
import { useAppSelector } from '@/redux/hooks'
import { useFilterOptionsQuery } from '../../api'
import { useSearch } from '../../hooks/useSearch'
import type {
  PropertyStringPath,
  SearchFilterState,
  SelectOptionFieldNamesOfStrings,
} from '../../types'

const EMPTY_ARRAY: readonly never[] = []

type ACProps = AutocompleteProps<
  string | { label: string },
  boolean,
  boolean,
  boolean,
  'div'
>

interface Props extends Omit<ACProps, 'renderInput' | 'options'> {
  label: string
  fieldName: SelectOptionFieldNamesOfStrings
  fieldPath: PropertyStringPath<SearchFilterState> & PropertyPath
  onChange?: () => void
}

const sortAsNumber = (a: string, b: string): number => {
  const aInt = Number.parseInt(a, 10)
  const bInt = Number.parseInt(b, 10)
  if ([aInt, bInt].every((n) => typeof n === 'number' && !Number.isNaN(n))) {
    return aInt < bInt ? -1 : 1
  } else {
    return a < b ? 0 : 1
  }
}

export const FilterAutocomplete = (props: Props) => {
  const { fieldName, fieldPath, label, multiple = true, ...restProps } = props
  const { data, ...filterRequest } = useFilterOptionsQuery(undefined, {
    selectFromResult: ({ data, ...rest }) => {
      if (!data || !data[fieldName]) return { data: EMPTY_ARRAY, ...rest }

      //handle string[]
      if (Array.isArray(data[fieldName])) {
        const set = new Set(data[fieldName])
        return {
          data: Array.from(set).sort(sortAsNumber),
          ...rest,
        }
      }

      //handle dictionary object
      if (!Array.isArray(data[fieldName])) {
        const values = Object.entries(data[fieldName])
          .map(([key, value]) => ({
            valueCode: key,
            label: `${value}`,
          }))
          .sort((a, b) => (a?.label < b?.label ? -1 : 1))
        return { data: values, ...rest }
      }

      return { data: EMPTY_ARRAY, ...rest }
    },
  })

  const { selectors, updateNext } = useSearch()
  const nextSearchOptions = useAppSelector(selectors.selectNextSearchParameters)

  const handleChange: ACProps['onChange'] = (
    _event,
    value,
    _reason,
    _details,
  ) => {
    updateNext(set({}, fieldPath, value))
  }

  const value = get(nextSearchOptions, fieldPath) ?? (multiple ? [] : '')

  return (
    <Autocomplete
      fullWidth
      options={data ?? ([] satisfies string[])}
      loading={filterRequest.isLoading}
      multiple={multiple}
      disableCloseOnSelect={multiple}
      autoHighlight
      value={value}
      onChange={handleChange}
      renderInput={(params) => <TextField {...params} label={label} />}
      {...restProps}
    />
  )
}
